import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Ban, LockOpen } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogPanel, DialogTitle } from "@/components/ui/dialog";

import type { ClaimWithRelations } from "../api/use-claim";
import {
  type BlockedTypeKey,
  blockedTypeOptions,
  blockedTypes,
  useResolveBlockedFlag,
  useUpsertBlockedFlag,
} from "./blocked-flag-banner";

const MIN_DESCRIPTION = 10;

function fmtDate(date: string | Date) {
  return format(new Date(date), "d MMMM yyyy", { locale: fr });
}

function fmtDateTime(date: string | Date) {
  return format(new Date(date), "d MMMM yyyy 'à' HH:mm", { locale: fr });
}

function initial(name?: string | null) {
  return (name ?? "?").trim().charAt(0).toUpperCase();
}

/** Port of blocked-flag/BlockedFlagConversation.vue — fil de commentaires du blocage. */
function BlockedFlagConversation({ claim }: { claim: ClaimWithRelations }) {
  const flag = claim.activeBlockedFlag ?? null;
  const [comment, setComment] = useState("");
  const upsertMutation = useUpsertBlockedFlag(claim.id);

  if (!flag) {
    return null;
  }

  function addComment() {
    const text = comment.trim();
    if (!text || upsertMutation.isPending) {
      return;
    }
    upsertMutation.mutate(
      { payload: { description: text }, successMessage: "Commentaire ajouté" },
      { onSuccess: () => setComment("") },
    );
  }

  return (
    <div className="text-left">
      {flag.modifications?.length ? (
        <div className="flex max-h-80 flex-col gap-2 overflow-y-auto">
          {/* Comments / type changes (the creation is shown in the modal header, not here) */}
          {flag.modifications.map((modification) => (
            <div
              key={modification.id}
              className="flex gap-2.5 rounded-lg border border-zinc-100 bg-zinc-50 px-3 py-2.5"
            >
              {modification.changedBy?.profilePictureUrl ? (
                <img
                  src={modification.changedBy.profilePictureUrl}
                  alt={modification.changedBy.fullName}
                  className="h-7 w-7 flex-shrink-0 rounded-full object-cover"
                />
              ) : (
                <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-zinc-200 text-[11px] font-semibold text-zinc-600">
                  {initial(modification.changedBy?.fullName)}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-[11px] text-zinc-400 tabular-nums">
                  {fmtDateTime(modification.changedAt)} ·{" "}
                  <span className="text-zinc-500">{modification.changedBy?.fullName}</span>
                </div>
                {modification.type ? (
                  <p className="mt-0.5 text-[11px] text-zinc-400 italic">
                    Type changé en « {blockedTypes[modification.type].displayText} »
                  </p>
                ) : null}
                <div className="mt-0.5 text-[13px] leading-normal whitespace-pre-wrap text-zinc-800">
                  {modification.description}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[13px] text-zinc-400">Aucun commentaire pour le moment.</p>
      )}

      {/* Composer */}
      <div className="mt-2.5 flex items-end gap-2">
        <textarea
          value={comment}
          onChange={(event) => setComment(event.target.value)}
          rows={2}
          placeholder="Ajouter un commentaire…"
          className="min-h-[62px] flex-1 resize-y rounded-md border border-zinc-300 px-[11px] py-[9px] text-[13px] leading-normal text-zinc-900 shadow-[inset_0_1px_2px_rgb(0_0_0_/_0.04)] outline-none placeholder:text-zinc-400 focus:border-blue-500 focus:ring-[3px] focus:ring-blue-500/20"
        />
        <Button
          size="sm"
          disabled={!comment.trim() || upsertMutation.isPending}
          loading={upsertMutation.isPending}
          onClick={addComment}
        >
          Envoyer
        </Button>
      </div>
    </div>
  );
}

interface BlockedFlagModalProps {
  claim: ClaimWithRelations;
  open: boolean;
  onClose: () => void;
}

/** Port of blocked-flag/BlockedFlagModal.vue — marquer/gérer un dossier bloqué. */
export function BlockedFlagModal({ claim, open, onClose }: BlockedFlagModalProps) {
  const flag = claim.activeBlockedFlag ?? null;

  const [selectedType, setSelectedType] = useState<BlockedTypeKey | null>(null);
  const [description, setDescription] = useState("");

  const upsertMutation = useUpsertBlockedFlag(claim.id);
  const resolveMutation = useResolveBlockedFlag(claim.id);
  const loading = upsertMutation.isPending || resolveMutation.isPending;

  const canCreate = Boolean(selectedType) && description.trim().length >= MIN_DESCRIPTION;

  function create() {
    if (!canCreate || loading || !selectedType) {
      return;
    }
    upsertMutation.mutate(
      {
        payload: { type: selectedType, description: description.trim() },
        successMessage: "Dossier marqué comme bloqué",
      },
      {
        onSuccess: () => {
          setSelectedType(null);
          setDescription("");
        },
      },
    );
  }

  function changeType(type: BlockedTypeKey) {
    if (loading || type === flag?.type) {
      return;
    }
    upsertMutation.mutate({
      payload: { type, description: `Type changé en « ${blockedTypes[type].displayText} »` },
      successMessage: "Type de blocage mis à jour",
    });
  }

  function resolve() {
    if (loading) {
      return;
    }
    resolveMutation.mutate(undefined, { onSuccess: () => onClose() });
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onClose();
        }
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Dossier bloqué</DialogTitle>
        </DialogHeader>

        <DialogPanel className="text-left">
          {flag ? (
            <div className="space-y-4">
              {/* MANAGE: an active flag already exists */}
              <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
                <div className="flex h-[38px] w-[38px] flex-shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
                  <Ban className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-amber-800">Dossier bloqué</span>
                    <span className="inline-flex items-center gap-1.5 rounded-md border border-amber-600/20 bg-white px-2 py-0.5 text-[11px] font-medium text-amber-700">
                      {(() => {
                        const { Icon } = blockedTypes[flag.type];
                        return <Icon className="h-3 w-3" />;
                      })()}
                      {blockedTypes[flag.type].displayText}
                    </span>
                  </div>
                  <div className="mt-1 text-[11.5px] text-[#a16207]">
                    Depuis le <span className="tabular-nums">{fmtDate(flag.blockedSince)}</span> · par{" "}
                    {flag.createdBy?.fullName}
                  </div>
                  <div className="mt-1.5 text-[13px] leading-normal whitespace-pre-wrap text-amber-900">
                    {flag.description}
                  </div>
                </div>
              </div>

              {/* Change type */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-zinc-500">Changer le type :</span>
                {blockedTypeOptions.map((option) => (
                  <Button
                    key={option.type}
                    variant="outline"
                    size="xs"
                    disabled={option.type === flag.type || loading}
                    onClick={() => changeType(option.type)}
                  >
                    <option.Icon />
                    {option.displayText}
                  </Button>
                ))}
              </div>

              <BlockedFlagConversation claim={claim} />

              <div className="flex justify-end border-t border-zinc-100 pt-3">
                <Button size="sm" disabled={loading} loading={resolveMutation.isPending} onClick={resolve}>
                  <LockOpen data-icon="inline-start" />
                  Débloquer le dossier
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* CREATE: no active flag */}
              <div>
                <span className="mb-2 block text-sm font-medium text-zinc-900">Type de blocage</span>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {blockedTypeOptions.map((option) => (
                    <Button
                      key={option.type}
                      variant={selectedType === option.type ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSelectedType(option.type)}
                    >
                      <option.Icon />
                      {option.displayText}
                    </Button>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="blocked-flag-description" className="block text-sm font-medium text-zinc-900">
                  Description (10 caractères minimum)
                </label>
                <textarea
                  id="blocked-flag-description"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  rows={3}
                  placeholder="Expliquez pourquoi ce dossier est bloqué…"
                  className="mt-2 block w-full rounded-md border border-zinc-300 px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-blue-500 focus:ring-[3px] focus:ring-blue-500/20 focus:outline-none"
                />
                <p className="mt-1 text-xs text-zinc-400">
                  Ce dossier sera retiré de la file de travail par défaut.
                </p>
              </div>

              <div className="flex justify-end">
                <Button disabled={!canCreate || loading} loading={upsertMutation.isPending} onClick={create}>
                  Marquer comme bloqué
                </Button>
              </div>
            </div>
          )}
        </DialogPanel>
      </DialogContent>
    </Dialog>
  );
}
